<template>
  <v-dialog v-model="dialog" width="759px">
    <v-card>
      <v-card-title class="headline">{{ modalTitle }}</v-card-title>

      <!-- modal body -->
      <div class="ma-4 mx-8 mb-0">

        <!-- Qr code type selection dropdown -->
        <div class="title font-bold mb-2 text-uppercase">
          {{ $t("qrcode_select_type") }}
        </div>
        <v-row class="pb-0">
          <v-col class="mb-0 pb-0">
            <v-select small dense :items="codeTypeOptions" v-model="selectedCodeType" item-text="text" item-value="value" @change="handleSelectChange" :disabled="isExistingGS1Code" outlined />
          </v-col>
        </v-row>

        <!-- standard code form -->
        <v-form v-if="isStandardCodeSelected" ref="standardCodeForm">
          <v-row v-if="isCodeSpacesEnabled" class="pb-0">
            <v-col class="mb-0 pt-1 pb-0">
              <div class="title font-bold text-uppercase mt-4 mb-2 ">
                {{ $t('qrcode_unique_identifier') }}
              </div>
              <div class="body-2 mb-3">
                {{ $t('qrcode_unique_identifier_description') }}
              </div>

              <div v-if="isExistingStandardCode" class="mb-3">
                {{ $t('qrcode_current_unique_identifier') }}: {{ selectedProductCode.message }}
              </div>

              <v-radio-group v-model="selectedUniqueIdentifier" class="mt-0">
                <div v-if="!isExistingStandardCode" class="mb-3">
                  <v-radio value="SYSTEM" :label="$t('qrcode_default_extended_id')"></v-radio>
                  <div v-if="selectedUniqueIdentifier === 'SYSTEM' && idTypeAtSubmit === 'SYSTEM' && isRequestInvalid && codeRenameRequestErrorMsg.length" class="mt-2 ml-8">
                    <div v-for="(message, idx) in codeRenameRequestErrorMsg" :key="idx" class="error-message">
                      {{ message }}
                    </div>
                  </div>
                </div>

                <div class="mb-3">
                  <v-radio value="PRODUCT_SKU" :label="$t('qrcode_product_sku')"></v-radio>
                  <div v-if="selectedUniqueIdentifier === 'PRODUCT_SKU'" class="ml-8">
                    <div class="body-2 grey--text text--darken-1">{{ $t('qrcode_product_sku_notification') }}</div>
                    <div v-if="isProductSkuTooShort" class="error-message mt-2" >
                      {{ $t('qrcode_product_sku_too_short') }}
                    </div>
                    <div v-if="isProductSkuCharsInvalid" class="error-message mt-2">
                      {{ $t('qrcode_field_product_sku_invalid') }}<br>
                      {{ $t('qrcode_field_unique_identifier_characters_allowed') }}.
                    </div>
                    <div v-if="idTypeAtSubmit === 'PRODUCT_SKU' && isRequestInvalid && codeRenameRequestErrorMsg.length" class="mt-2">
                      <div v-for="(message, idx) in codeRenameRequestErrorMsg" :key="idx" class="error-message">
                        {{ message }}
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <v-radio value="CUSTOM" :label="$t('qrcode_custom_identifier')"></v-radio>
                  <div v-if="selectedUniqueIdentifier === 'CUSTOM'">
                    <v-text-field v-model="customIdentifier" :rules="uniqueIdentifierRules" :placeholder="$t('qrcode_custom_identifier_placeholder')" :error-messages="idTypeAtSubmit === 'CUSTOM' ? codeRenameRequestErrorMsg : []" required small dense outlined class="mt-1 ml-8"></v-text-field>
                  </div>
                </div>
              </v-radio-group>
            </v-col>
          </v-row>
          <v-card-text v-if="!isEditingCode && selectedCodeType" class="pa-0 mt-9">
            <div class="title font-bold text-uppercase mt-4 mb-3">
              {{ $t("qrcode_how_many_codes") }}
            </div>

            <v-text-field :label="$t('qrcode_quantity')" ref="quantityField" v-model.number="quantity" type="number" :rules="quantityRules" required small dense outlined></v-text-field>
            <div class="error-message" v-if="hasOnDemandError">
              {{ $t("qrcode_quota_reached")}}
            </div>
          </v-card-text>
        </v-form>

        <!-- GS1 digital link form -->
        <v-form v-if="isGS1CodeSelected" v-model="isGs1CodeFormValid" ref="gs1CodeForm" class="pt-0">
          <div class="title font-bold text-uppercase mt-4 mb-2 d-flex">
            <span class="mr-2">{{ $t('qrcode_with_gs1_digital_link') }}</span>
            <v-menu open-on-hover right offset-x :close-delay="200" :close-on-content-click="false" max-width="450" content-class="gs1-help-menu">
              <template v-slot:activator="{ on, attrs }">
                <v-icon color="primary" v-bind="attrs" v-on="on">mdi-help-circle</v-icon>
              </template>
              <div class="d-flex align-center">
                <div class="mr-4">
                  <div class="mb-3">{{ $t('qrcode_gs1_print_distance_hint') }}</div>
                  <i18n path="qrcode_gs1_digital_link_learn_more" tag="div" scope="global">
                    <template v-slot:gs1_digital_link_codes>
                      <a :href="gs1DigitalLinkLearnMoreLink" @click.prevent="openHelpCenterArticle(gs1DigitalLinkLearnMoreLink)" target="_blank" rel="noopener" class="text-decoration-none">{{ $t('qrcode_gs1_digital_link_codes') }}</a>
                    </template>
                  </i18n>
                </div>
                <img :src="gs1PrintDistanceImage" width="180" :alt="$t('qrcode_gs1_print_distance_hint')">
              </div>
            </v-menu>
          </div>
          <!-- GTIN field -->
          <v-row class="mt-0">
            <v-col md="6">
              <v-label class="black--text">
                <span class="font-weight-bold mr-2">(01) {{ $t('qrcode_gtin') }}*</span>
                <span class="caption">{{ $t('qrcode_products_gtin') }}</span>
              </v-label>
              <v-text-field
                small
                dense
                outlined
                :counter="14"
                :rules="gtinRules"
                :class="{ 'invalid-check-digit-outline': isGtinCheckDigitValid === false }"
                required
                v-model="form.gtin"
                :disabled="disableFormFields"
              />
              <div v-if="isGtinCheckDigitValid !== null" class="mb-2">
                <div v-if="isGtinCheckDigitValid" class="d-flex align-start">
                  <v-icon color="green" class="mr-2">mdi-check-circle</v-icon>
                  <span class="font-weight-regular info-text">
                    {{ $t('qrcode_gtin_valid') }}
                  </span>
                </div>

                <div v-else class="d-flex align-start">
                  <v-icon color="orange" class="mr-2">mdi-alert-circle</v-icon>
                  <span class="font-weight-regular info-text">
                    {{ $t('qrcode_gtin_invalid') }}
                    <a
                      class="text-decoration-none"
                      :href="'https://www.gs1.org/services/check-digit-calculator'"
                      target="_blank"
                      rel="noopener"
                    >
                      {{ $t('qrcode_check_digits_learn_more') }}
                    </a>
                  </span>
                </div>
              </div>
            </v-col>

            <!-- lot field -->
            <v-col md="6">
              <v-label class="black--text">
                <span class="font-weight-bold mr-2">(10) {{ $t('qrcode_lot') }}</span>
                <span class="caption">{{ $t('qrcode_lot_batch_number') }}</span>
              </v-label>
              <v-text-field
                small
                dense
                outlined
                persistent-hint
                :counter="20"
                :rules="fieldRules"
                v-model="form.lot"
                :required="false"
                :disabled="disableFormFields"
              >
                <template v-slot:append>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="{
                          ...on,
                          click: handleRefreshIconClick
                        }" :disabled="disableFormFields">
                        mdi-refresh
                      </v-icon>
                    </template>
                    <span class="black--text body-2">{{ $t('qrcode_generate_batch_number') }}</span>
                  </v-tooltip>
                </template>
              </v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <!-- CPV field -->
            <v-col md="6">
              <v-label class="black--text">
                <span class="font-weight-bold mr-2">(22) {{ $t('qrcode_cpv') }}</span>
                <span class="caption">{{ $t('qrcode_consumer_product_variant_vintage_year') }}</span>
              </v-label>
              <v-text-field
                small
                dense
                outlined
                persistent-hint
                :counter="20"
                v-model="form.cpv"
                :rules="fieldRules"
                :required="false"
                :disabled="disableFormFields"
              />
            </v-col>

            <!-- Serial number field -->
            <v-col md="6">
              <v-label class="black--text">
                <span class="font-weight-bold mr-2">(21) {{ $t('qrcode_serial_number') }}</span>
              </v-label>
              <v-text-field
                small
                dense
                outlined
                persistent-hint
                :counter="20"
                v-model="form.serial"
                :rules="serialRules"
                :required="false"
                :disabled="disableFormFields"
              />
            </v-col>
          </v-row>

          <!-- Data attributes row -->
          <div class="d-flex flex-column align-start mt-4 mb-1">
            <v-label class="black--text">
              <span class="font-weight-bold mr-2">{{ $t('qrcode_gs1_data_attributes') }}</span>
              <span class="caption">{{ $t('qrcode_data_attributes_caption') }}</span>
            </v-label>
            <v-btn v-if="form.data_attributes.length < dataAttributeOptions.length" @click="addDataAttribute()" color="primary" class="my-1 pa-2" text large>
              <v-icon class="mr-2" color="primary">mdi-plus-circle</v-icon>
              {{ $t('qrcode_add_data_attributes') }}
            </v-btn>
          </div>
          <v-row v-for="(attribute, index) of form.data_attributes" :key="index">
            <v-col class="pb-0">
              <v-select
                small
                dense
                outlined
                v-model="attribute.key"
                :items="dataAttributeOptions"
                item-text="text"
                item-value="value"
                :rules="dataAttributeKeyRules(attribute)"
                :disabled="disableFormFields"
              />
            </v-col>
            <v-col class="pb-0">
              <v-text-field
                small
                dense
                outlined
                persistent-hint
                :counter="2"
                v-model="attribute.value"
                :rules="dataAttributeValueRules(attribute)"
                :disabled="disableFormFields"
              />
            </v-col>
            <v-col cols="auto" class="px-0">
              <v-btn color="red" @click="removeDataAttribute(index)" icon><v-icon>mdi-close-circle</v-icon></v-btn>
            </v-col>
          </v-row>

          <!-- disables all fields -->
          <v-row class="mt-4">
            <v-col class="border border-primary d-flex flex-column" v-if="gs1CodeExtendedId" align-center>
              <div class="d-flex flex-row fill-height">
                <v-chip class="align-self-center pa-0 px-1 mr-1" label color="#002C6C" text-color="white" small>
                  {{ $t('qrcode_gs1') }}
                </v-chip>
                <span :class="[!isGs1CodeFormValid ? 'error-message' : 'gs1-label-text', 'align-self-center']">
                  {{gs1CodeExtendedId}}{{ dataAttributesStr ? `?${dataAttributesStr}` : '' }}
                </span>
              </div>
              <div class="mt-2" v-if="idTypeAtSubmit === 'GS1' && isRequestInvalid && codeRenameRequestErrorMsg.length">
                <div v-for="(message, idx) in codeRenameRequestErrorMsg" :key="idx" class="error-message mb-1">
                  {{ message }}
                </div>
              </div>
              <div class="error-message" v-if="hasOnDemandError">
                {{ $t("qrcode_quota_reached")}}
              </div>
            </v-col>
          </v-row>

          <!-- tip text -->
          <v-row class="mt-2 mb-4">
            <v-col>
              <div class="info-text">
                <span class="font-weight-bold mr-2">{{ $t('qrcode_tip') }}:</span>
                <i18n
                  path="qrcode_info_text"
                  tag="label"
                  class="font-weight-regular"
                  scope="global"
                >
                  <template v-slot:qrcode_ingestion_feature>
                    <a
                      :href="helpCenterLink"
                      @click.prevent="openHelpCenterArticle(helpCenterLink)"
                      target="_blank"
                      class="text-decoration-underline info-text font-weight-regular"
                      rel="noopener noreferrer"
                    >
                      {{ $t('qrcode_ingestion_feature') }}
                    </a>
                  </template>
                  <template v-slot:product_field_below>
                    {{ $t('product_field_below') }}
                  </template>
                </i18n>
              </div>
            </v-col>
          </v-row>
        </v-form>
      </div>

      <!-- modal action buttons -->
      <v-card-actions class="pa-4 px-8 mt-3 pb-6">
        <v-spacer></v-spacer>
        <v-btn class="mr-6" @click="closeModal" color="primary" text>
          {{$t('cancel')}}
        </v-btn>
        <v-btn
          class="primary"
          :disabled="disableGenerateButton"
          :loading="isCreatingCode"
          @click="handleActionButtonClick">
          {{isEditingCode ? $t('save') : $t('generate_qr_code')}}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Utils from '@/utils/utils'
import { openHelpCenterArticle } from '@/utils/helpCenter'
import { mapGetters, mapMutations } from 'vuex'
import amplitude from 'amplitude-js'

export default {
  name: 'generate-qrcode-dialog',
  data: () => ({
    selectedCodeType: null,
    selectedUniqueIdentifier: 'SYSTEM',
    customIdentifier: '',
    idTypeAtSubmit: '',
    isGs1CodeFormValid: false,
    batchSize: 10,
    quantity: 1,
    form: {
      gtin: '',
      cpv: '',
      lot: '',
      serial: '',
      data_attributes: []
    },
    helpCenterLink: 'https://help.scantrust.com/hc/en-us/articles/11328726701084-e-label-Editor-QR-Codes-GS1-Digital-Link-Preparation-and-Generation-in-Bulk-Enterprise'
  }),
  props: {
    displayDialog: {
      type: Boolean,
      required: true
    },
    selectedProduct: {
      type: Object,
      required: true
    },
    isConvertingToGs1: {
      type: Boolean,
      required: false
    }
  },
  computed: {
    ...mapGetters(['totalActiveCodesCount', 'selectedCampaign', 'isCreatingCode', 'codes', 'hasCodeRenameError', 'codeRenameRequestErrorMsg', 'isEditingCode', 'selectedProductCode', 'profile', 'hasOnDemandError', 'language']),
    dialog: {
      get () {
        return this.displayDialog
      },
      set (value) {
        this.$emit('update:displayDialog', value)
      }
    },
    codeTypeOptions () {
      return [
        { text: this.$t('qrcode_standard_qr_code'), value: Utils.CODE_TYPE.DEFAULT },
        { text: this.$t('qrcode_with_gs1_digital_link'), value: Utils.CODE_TYPE.GS1 }
      ]
    },
    isExistingStandardCode () {
      return this.selectedProductCode?.message && !Utils.isGS1Identifier(this.selectedProductCode.message)
    },
    isExistingGS1Code () {
      return this.selectedProductCode?.message && Utils.isGS1Identifier(this.selectedProductCode.message)
    },
    isGS1CodeSelected () {
      return this.selectedCodeType === Utils.CODE_TYPE.GS1
    },
    isStandardCodeSelected () {
      return this.selectedCodeType === Utils.CODE_TYPE.DEFAULT
    },
    isProductSkuTooShort () {
      return this.selectedUniqueIdentifier === 'PRODUCT_SKU' && this.selectedProduct.sku.length < 8
    },
    isProductSkuCharsInvalid () {
      return this.selectedUniqueIdentifier === 'PRODUCT_SKU' && !/^[A-Za-z0-9_-]+$/.test(this.selectedProduct.sku || '')
    },
    uniqueIdentifierRules () {
      return [
        v => !!v || this.$t('field_required'),
        v => !v || v.length >= 8 || this.$t('minimum_characters_required', { count: 8 }),
        v => !v || v.length <= 36 || this.$t('maximum_characters_allowed', { count: 36 }),
        v => /^[A-Za-z0-9_-]+$/.test(v) || this.$t('qrcode_field_unique_identifier_characters_allowed')
      ]
    },
    quantityRules () {
      return [
        v => !!v || this.$t('qrcode_quantity_required'),
        v => v > 0 || this.$t('qrcode_quantity_minimum'),
        v => !(v > 1 && this.selectedUniqueIdentifier !== 'SYSTEM') ||
        this.$t('qrcode_quantity_duplicate_ids')
      ]
    },
    gtinRules () {
      return [
        v => !!v || this.$t('qrcode_gtin_required'),
        v => /^[0-9]+$/.test(v) || this.$t('qrcode_field_numeric_allowed'),
        v => v.length === 14 || this.$t('qrcode_gtin_fourteen_characters')
      ]
    },
    isGtinCheckDigitValid () {
      if (this.form.gtin && this.form.gtin.length === 14) {
        const first13 = this.form.gtin.substring(0, 13)
        const providedCheck = parseInt(this.form.gtin.charAt(13), 10)
        const computed = this.calculateGtinCheckDigit(first13)
        return computed === providedCheck
      }
      return null
    },
    fieldRules () {
      return [
        v => !v || v.length >= 2 || this.$t('minimum_characters_required', { count: 2 }),
        v => !v || v.length <= 20 || this.$t('qrcode_field_twenty_characters_maximum'),
        v => !v || Utils.isAlphaNumeric(v) || this.$t('qrcode_field_numeric_alpha_allowed')
      ]
    },
    serialRules () {
      return [
        v => !v || v.length >= 2 || this.$t('minimum_characters_required', { count: 2 }),
        v => !v || v.length <= 20 || this.$t('maximum_characters_allowed', { count: 20 }),
        v => !v || /^[a-zA-Z0-9"._!+,*[\]';:=@$()><-]+$/.test(v) || this.$t('qrcode_field_serial_number_characters_allowed')
      ]
    },
    sortedCodes () {
      const sortedArray = this.codes
      return sortedArray.sort((a, b) => {
        return new Date(b.activated_at) - new Date(a.activated_at)
      })
    },
    gs1CodeExtendedId () {
      const segments = []
      if (this.form.gtin) segments.push(`${Utils.AI.GTIN}/${this.form.gtin}`)
      if (this.form.cpv) segments.push(`${Utils.AI.CPV}/${this.form.cpv}`)
      if (this.form.lot) segments.push(`${Utils.AI.LOT_BATCH_NUMBER}/${this.form.lot}`)
      if (this.form.serial) segments.push(`${Utils.AI.SERIAL_NUMBER}/${this.form.serial}`)
      return segments.join('/')
    },
    dataAttributeOptions () {
      return [
        { text: this.$t('qrcode_data_attribute_20'), value: 20 }
      ]
    },
    dataAttributesStr () {
      const validDataAttributes = this.form.data_attributes.filter(attribute => attribute.key && attribute.value)
      return validDataAttributes.map(attribute => `${attribute.key}=${attribute.value}`).join('&')
    },
    disableFormFields () {
      return this.isGS1CodeSelected && this.isCreatingCode
    },
    isRequestInvalid () {
      return this.hasCodeRenameError
    },
    disableGenerateButton () {
      if (this.isStandardCodeSelected) {
        if (!this.selectedUniqueIdentifier) {
          return true
        }
        if (!this.isProductSkuTooShort && !this.isProductSkuCharsInvalid) {
          return false
        }
      } else if (this.isGS1CodeSelected && this.isGs1CodeFormValid) {
        return false
      } else if (this.isCreatingCode) {
        return true
      }
      return true
    },
    modalTitle () {
      if (!this.isEditingCode) {
        return this.$t('qrcode_new_codes')
      }

      if (this.isGS1CodeSelected) {
        return this.isExistingStandardCode ? this.$t('qrcode_convert_to_gs1_link') : this.$t('qrcode_edit_gs1_link')
      } else {
        return this.$t('qrcode_edit_unique_identifier')
      }
    },
    isCodeSpacesEnabled () {
      return this.profile?.company?.code_spaces_enabled
    },
    gs1DigitalLinkLearnMoreLink () {
      switch (this.language) {
        case 'de':
          return 'https://help.scantrust.com/hc/de/articles/11559244732572-e-label-Editor-QR-Code-Erstellen-von-GS1-Digital-Link-e-label-Codes'
        case 'es':
          return 'https://help.scantrust.com/hc/es-419/articles/11559244732572-e-label-Editor-C%C3%B3digo-QR-Creaci%C3%B3n-de-c%C3%B3digos-de-e-label-y-GS1-Digital-Link'
        case 'fr':
          return 'https://help.scantrust.com/hc/fr/articles/11559244732572-e-label-%C3%89diteur-QR-Code-Cr%C3%A9ation-de-codes-d-e-label-et-GS1-Digital-Link'
        case 'it':
          return 'https://help.scantrust.com/hc/it/articles/11559244732572-e-label-Editore-Codice-QR-Creazione-di-codici-di-e-label-e-GS1-Digital-Link'
        case 'pt':
          return 'https://help.scantrust.com/hc/pt/articles/11559244732572-e-label-Editor-QR-Code-Cria%C3%A7%C3%A3o-de-c%C3%B3digos-de-e-label-GS1-Digital-Link'
        default:
          return 'https://help.scantrust.com/hc/en-us/articles/11559244732572-e-label-Editor-QR-Code-Creating-GS1-Digital-Link-e-label-Codes'
      }
    },
    gs1PrintDistanceImage () {
      return require('@/assets/gs1-qr-print-distance.png')
    }
  },
  methods: {
    ...mapMutations({
      setTotalActiveCount: 'SET_TOTAL_ACTIVE_CODES_COUNT',
      setIsCreatingCode: 'SET_IS_CREATING_CODE',
      setRenameCodeErrorMessage: 'SET_RENAME_CODE_ERROR_MESSAGE',
      setRenameCodeError: 'SET_RENAME_CODE_ERROR',
      setOnDemandError: 'SET_ON_DEMAND_CODE_ERROR',
      setSelectedProductCode: 'SET_SELECTED_PRODUCT_CODE',
      setCodeEditing: 'SET_CODE_EDITING'
    }),
    handleSelectChange () {
      if (this.isStandardCodeSelected && !this.isExistingStandardCode && !this.selectedUniqueIdentifier) {
        this.selectedUniqueIdentifier = 'SYSTEM'
      }
    },
    closeModal () {
      this.dialog = !this.dialog
    },
    calculateGtinCheckDigit (gtin13) {
      if (gtin13.length !== 13) {
        return null
      }
      // Predefined multiplier array for up to 17 digits
      const multiply = [3, 1, 3, 1, 3, 1, 3, 1, 3, 1, 3, 1, 3, 1, 3, 1, 3]
      const subMultipliers = multiply.slice(multiply.length - 13)
      let sum = 0
      for (let i = 0; i < gtin13.length; i++) {
        sum += parseInt(gtin13[i], 10) * subMultipliers[i]
      }

      const nearestMultipleOfTen = Math.round(sum / 10) * 10
      let checkDigit = nearestMultipleOfTen - sum
      if (checkDigit < 0) {
        checkDigit = (nearestMultipleOfTen + 10) - sum
      }
      return checkDigit
    },
    addDataAttribute () {
      this.form.data_attributes.push({ key: '', value: '' })
    },
    removeDataAttribute (index) {
      this.form.data_attributes.splice(index, 1)
    },
    dataAttributeKeyRules (attribute) {
      return [
        v => !attribute.value || !!v || this.$t('qrcode_select_key_if_value')
      ]
    },
    dataAttributeValueRules (attribute) {
      return [
        v => !attribute.key || !!v || this.$t('qrcode_enter_value_if_key'),
        v => !v || Utils.isNumeric(v) || this.$t('qrcode_field_numeric_allowed'),
        v => !v || v.length === 2 || this.$t('qrcode_field_two_numbers')
      ]
    },
    async handleActionButtonClick () {
      await this.$nextTick() // ensure the latest radio selection is read if users click fast
      const tags = {}
      this.setRenameCodeErrorMessage({ errorMessage: [] })
      this.setRenameCodeError(false)
      this.setOnDemandError(false)

      if (this.isStandardCodeSelected && (this.isProductSkuTooShort || this.isProductSkuCharsInvalid)) {
        return
      }

      if (this.isStandardCodeSelected && this.$refs.standardCodeForm.validate()) {
        this.idTypeAtSubmit = this.selectedUniqueIdentifier

        if (this.isExistingStandardCode) {
          const extendedId = this.selectedUniqueIdentifier === 'CUSTOM' ? this.customIdentifier : this.selectedProduct.sku
          await this.renameExtendedId(extendedId, tags, false)
        } else {
          await this.generateStandardCode()
        }
        return
      }

      if (this.isGS1CodeSelected && this.$refs.gs1CodeForm.validate()) {
        this.idTypeAtSubmit = 'GS1'

        tags.gs1_gtin = this.form.gtin
        if (this.form.cpv) tags.gs1_cpv = this.form.cpv
        if (this.form.lot) tags.gs1_lot = this.form.lot
        if (this.form.serial) tags.gs1_serial = this.form.serial

        tags.gs1_data_attributes = this.dataAttributesStr

        if (this.isConvertingToGs1) {
          await this.renameExtendedId(this.gs1CodeExtendedId, tags, true)
        } else {
          await this.generateGS1Code(this.selectedProduct, this.gs1CodeExtendedId, tags)
        }
      }
    },
    async createBatch (quantity) {
      if (!quantity) {
        return Promise.resolve()
      }

      const batchQuantity = quantity <= this.batchSize ? quantity : this.batchSize
      const promisesArray = []
      for (var i = 0; i < batchQuantity; i++) {
        promisesArray.push(this.$store.dispatch('generateOnDemandCode', { product: this.selectedProduct }))
      }

      const results = await Promise.allSettled(promisesArray)
      const rejected = results.filter(result => result.status === 'rejected')
      const successCount = batchQuantity - rejected.length

      // Rate limited: back off before retrying only the codes that did not succeed this round.
      const isRateLimited = rejected.some(result => result.reason?.response?.status === 429)
      if (isRateLimited) {
        await new Promise(resolve => setTimeout(resolve, 10000))
        await this.createBatch(quantity - successCount)
        return
      }

      // Any other failure (e.g. quota.exceeded) is persistent: stop and keep the codes already
      // created. The store action has set the quota error flag, surfacing the "quota reached"
      // message, so retrying would only loop and hammer the API.
      if (rejected.length) {
        return
      }

      const remainingQuantity = quantity - batchQuantity
      if (remainingQuantity > 0) {
        await this.createBatch(remainingQuantity)
      }
    },
    async generateStandardCode () {
      this.setIsCreatingCode(true)
      if (this.quantity === 1) {
        try {
          let code = {}
          code = await this.$store.dispatch('generateOnDemandCode', { product: this.selectedProduct })
          if (this.selectedUniqueIdentifier !== 'SYSTEM') {
            await this.$store.dispatch('renameCode', {
              product: this.selectedProduct,
              source: code,
              target: this.selectedUniqueIdentifier === 'CUSTOM' ? this.customIdentifier : this.selectedProduct.sku,
              isGS1: false
            })
          }
          code.isNew = true
          setTimeout(() => {
            code.isNew = false
          })
          amplitude.getInstance().logEvent('create QR codes', { quantity: this.quantity })
          this.setTotalActiveCount(parseInt(this.totalActiveCodesCount) + parseInt(this.quantity))
          this.dialog = false
        } catch (err) {
          console.log(err)
        } finally {
          this.setIsCreatingCode(false)
        }
      } else if (this.quantity > 1) {
        try {
          await this.createBatch(this.quantity)
          this.setIsCreatingCode(false)
          await this.$store.dispatch('loadProductCodeCount', { product: this.selectedProduct.id })
          await this.$store.dispatch('loadCodes', { campaign: this.selectedCampaign.id, product: this.$route.params.id, offset: 0, noFilter: true })
          this.sortedCodes.forEach((code, index) => {
            if (index < this.quantity) {
              code.isNew = true
            }
          })
          amplitude.getInstance().logEvent('create QR codes', { quantity: this.quantity })
          this.setTotalActiveCount(parseInt(this.totalActiveCodesCount) + parseInt(this.quantity))
          this.dialog = false
        } catch (err) {
          console.log(err)
        } finally {
          this.setIsCreatingCode(false)
        }
      }
    },
    handleRefreshIconClick () {
      this.form.lot = Utils.generateLotNumber()
    },
    openHelpCenterArticle,

    async renameExtendedId (extendedId, tags, isGS1) {
      this.setIsCreatingCode(true)
      try {
        await this.$store.dispatch('renameCode', { source: this.selectedProductCode, target: extendedId, tags, isGS1 })
        this.dialog = false
      } catch (err) {
        console.log('err', err)
      } finally {
        this.setIsCreatingCode(false)
      }
    },

    async generateGS1Code (product, gs1CodeExtendedId, tags) {
      try {
        this.setIsCreatingCode(true)
        if (this.isEditingCode) {
          const isExtendedIdChanged = this.selectedProductCode?.message !== gs1CodeExtendedId
          const savedDataAttributesStr = this.selectedProductCode.scm_data.gs1_data_attributes || ''
          if (isExtendedIdChanged) {
            // Rename the code the user opened. Creating one here would leave the original
            // untouched and add a second code to the list.
            await this.$store.dispatch('renameCode', {
              source: this.selectedProductCode,
              target: gs1CodeExtendedId,
              tags,
              isGS1: true
            })
          } else if (this.dataAttributesStr !== savedDataAttributesStr) {
            await this.$store.dispatch('updateScmData', { extendedId: this.selectedProductCode.message, tags })
          }
        } else {
          const code = await this.$store.dispatch('generateOnDemandCode', { product: this.selectedProduct })
          await this.$store.dispatch('renameCode', {
            product,
            source: code,
            target: gs1CodeExtendedId,
            tags,
            isGS1: true
          })
          code.isNew = true
          setTimeout(() => {
            code.isNew = false
          })
          this.setTotalActiveCount(parseInt(this.totalActiveCodesCount) + 1)
        }

        amplitude.getInstance().logEvent('create GS1 QR codes')
        this.dialog = false
      } catch (e) {
        console.log('err', e)
      } finally {
        this.setIsCreatingCode(false)
      }
    },

    loadGs1DataInForm () {
      const matchPattern = /^01\/(\d{14})(\/22\/([a-zA-Z0-9]+))?(\/10\/([a-zA-Z0-9]+))?(\/21\/([a-zA-Z0-9"._!+,*[\]';:=@$()><-]+))?/
      const message = matchPattern.exec(this.selectedProductCode?.message)

      if (message) {
        this.form.gtin = message[1]
        if (message[3]) this.form.cpv = message[3]
        if (message[5]) this.form.lot = message[5]
        if (message[7]) this.form.serial = message[7]
      }

      const attributesStr = this.selectedProductCode?.scm_data.gs1_data_attributes || ''
      const attributePairs = attributesStr.split('&').filter(pair => pair !== '')
      this.form.data_attributes = attributePairs.map(pair => {
        const [keyStr, value] = pair.split('=')
        return {
          key: parseInt(keyStr),
          value
        }
      })
    }

  },
  watch: {
    // potentially clear error for quantity field if selecting another type of unique identifier
    selectedUniqueIdentifier () {
      this.$nextTick(() => {
        if (this.$refs.quantityField) this.$refs.quantityField.validate()
      })
    }
  },
  created () {
    if (!this.isCodeSpacesEnabled || (this.isExistingStandardCode && !this.isConvertingToGs1)) {
      this.selectedCodeType = Utils.CODE_TYPE.DEFAULT
      return
    }

    if (this.isExistingGS1Code || this.isConvertingToGs1) {
      this.selectedCodeType = Utils.CODE_TYPE.GS1

      if (this.isExistingGS1Code) {
        this.loadGs1DataInForm()
      }
    }
  },
  beforeDestroy () {
    this.setSelectedProductCode(null)
    this.setCodeEditing(false)
    if (this.codeRenameRequestErrorMsg.length) {
      this.setRenameCodeErrorMessage({ errorMessage: [] })
      this.setRenameCodeError(false)
      this.setOnDemandError(false)
    }
  }
}
</script>

<style lang="sass" scoped>
.error-message
  color: #F22A42
  font-size: 14px
  font-weight: 300

.invalid-check-digit-outline
  ::v-deep .v-input__slot fieldset
    color: #ff5252 !important

.gs1-label-text
  color: #999999
  font-size: 14px
  font-style: normal
  font-weight: 400
  line-height: 24px
  letter-spacing: 0.15px

.info-text
  color: #777777
  font-size: 16px
  font-style: normal
  font-weight: 700
  line-height: 24px
  letter-spacing: 0.15px

.v-tooltip__content
  pointer-events: initial
</style>
